import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import {
  SectionHeader, WinRateTable, CALL_BUCKETS, PUT_BUCKETS,
  TP_CALL_BE, TP_PUT_BE, tpRateColor, WINRATE_GLOW_RGB,
} from './shared';
import { TP_ANCHORS } from './AssessmentDataContext';

// Extracted verbatim from Assessment.js's `activeTab === 'take_profit'`
// render block (Tech Debt 2026-09, "Assessment and Backtest are god files"
// -- remaining-7-tabs context lift). Reads shared state from
// useAssessmentData() instead of via props.

export default function TakeProfitTab() {
  const {
    dteCfg, selectedWindow, dteStrategy, tpAnchor, setTpAnchorPersist,
    tpLoading, tpData, tpError, activeTpByBucket, showCalls, showPuts,
  } = useAssessmentData();
  return (
  <>
    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
      <SectionHeader>
        Take Profit % by bucket × period — {dteCfg.label}{selectedWindow ? ` · ${selectedWindow} window` : ' · all data'}
      </SectionHeader>
      {/* Anchor toggle (2026-08-10 dual-anchor reanchor) — legacy vs live canon */}
      <div className="flex items-center bg-trading-dark-900 border border-white/[0.08] rounded-md p-0.5">
        {Object.entries(TP_ANCHORS).map(([k, cfg]) => {
          const isDisabled = k === 'tp26' && dteStrategy === '15';
          return (
            <button
              key={k}
              type="button"
              disabled={isDisabled}
              onClick={() => setTpAnchorPersist(k)}
              className={`px-3 py-1.5 text-[11px] font-medium rounded whitespace-nowrap ${
                isDisabled
                  ? 'text-gray-700 cursor-not-allowed'
                  : tpAnchor === k
                    ? 'bg-trading-blue-600/20 text-trading-blue-300'
                    : 'text-gray-500'
              }`}
              title={isDisabled ? '2026 Canon is 30 DTE only — no canon values exist for 15 DTE' : cfg.title}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
    <div className="mb-4 space-y-3">
      <div className="p-3 bg-trading-blue-500/[0.04] border border-trading-blue-500/20 rounded text-[12px] text-gray-400 leading-relaxed">
        <div className="text-trading-blue-300 font-medium mb-1">What this measures</div>
        Probability the option <span className="text-gray-200 font-medium">hits TP before SL</span> within W days,
        using the assessment&apos;s <span className="text-gray-200 font-medium">fixed option-anchor barriers</span>{' '}
        (<span className="text-trading-blue-300 font-medium">{dteStrategy === '15' ? '15 DTE C1' : tpAnchor === 'tp26' ? '30 DTE 2026 canon' : '30 DTE H5'}</span> era:{' '}
        {dteStrategy === '15' ? 'TP=+35%/SL=−30%' : tpAnchor === 'tp26' ? 'TP=+10%/SL=−100%' : 'TP=+35%/SL=−30%'} calls, TP=+35%/SL=−20% puts on premium).
        {tpAnchor === 'tp26' ? (
          <span className="text-trading-green-300/80"> This anchor matches the live 2026-08-10 Core/Apex canon —
          puts are shown for reference only (unchanged, still off portfolio-wide). Switch to{' '}
          <span className="text-gray-300">Legacy</span> for cross-version comparability (it feeds the W1-W6 ship
          gates and is kept byte-identical).</span>
        ) : (
          <span className="text-amber-300/80"> These anchors are deliberately frozen for cross-version comparability —
          they are a measurement instrument, not the live strategy (which moved to TP+10% calls on 2026-08-10;
          switch to <span className="text-gray-300">2026 Canon</span> above to measure at the live barriers).</span>
        )}
        <br />
        <span className="text-gray-500">
          Win Rate uses generic K=2σ/M=5σ directional barriers (does the underlying move enough?);
          TP% uses tighter option-aligned barriers (would the option have actually profited?).
          Same peaks, two different questions. Cells are most accurate at the highlighted period
          ({dteStrategy === '15' ? 'WR15' : 'WR30'}); shorter periods slightly under-state realized
          option gain because the √-scaled barrier-touch model ignores theta decay.
        </span>
      </div>
      <div className="bg-trading-dark-900 border border-white/[0.06] rounded-md px-4 py-3 font-mono text-[11px] text-gray-400 space-y-2 tabular-nums min-h-[360px]">
        {dteStrategy === '15' ? (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-300 font-sans">
              15 DTE C1 assessment anchor <span className="text-gray-500 normal-case tracking-normal">(Phase 15B era, frozen — not the live strategy)</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-green-300">Calls — TP +35% / SL −30% on premium</span>
              <span>win if high ≥ <span className="text-gray-100">entry × (1 + 0.903σ·√(W/15))</span></span>
              <span className="text-gray-600">stop if low ≤ <span className="text-gray-400">entry × (1 − 0.774σ·√(W/15))</span></span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-red-300">Puts — TP +35% / SL −20% on premium</span>
              <span>win if low ≤ <span className="text-gray-100">entry × (1 − 0.903σ·√(W/15))</span></span>
              <span className="text-gray-600">stop if high ≥ <span className="text-gray-400">entry × (1 + 0.516σ·√(W/15))</span></span>
            </div>
            <div className="text-gray-500 pt-1 border-t border-white/[0.06] leading-relaxed">
              σ targets derive from <span className="text-gray-400">TP × PREMIUM_MULT / Δ</span> with PREMIUM_MULT = 1.29 (15 DTE ATM ≈ 1.29×σ_daily) and Δ = 0.5.
              Hold = 7 trading bars; no portfolio drawdown entry halt is applied.
              <br />Anchor ref = 15 cal days. Scale √(W/15): 3d ≈ 0.45×{' · '}5d ≈ 0.58×{' · '}7d ≈ 0.68×{' · '}15d = 1.0× (anchor){' · '}30d ≈ 1.41×.
              <br />1d = direction only (no barrier).
            </div>
          </>
        ) : tpAnchor === 'tp26' ? (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-300 font-sans">
              30 DTE 2026 canon assessment anchor <span className="text-gray-500 normal-case tracking-normal">(2026-08-10 TP/SL retune era — matches the live Core/Apex strategy)</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-green-300">Calls — TP_BASE +10% / SL_BASE −100% on premium</span>
              <span>win if high ≥ <span className="text-gray-100">entry × (1 + 0.364σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if low ≤ <span className="text-gray-400">entry × (1 − 3.64σ·√(W/30))</span></span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-red-300">Puts — TP +35% / SL −20% on premium <span className="text-gray-600 normal-case">(unchanged — not retuned, off portfolio-wide)</span></span>
              <span>win if low ≤ <span className="text-gray-100">entry × (1 − 1.274σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if high ≥ <span className="text-gray-400">entry × (1 + 0.728σ·√(W/30))</span></span>
            </div>
            <div className="text-gray-500 pt-1 border-t border-white/[0.06] leading-relaxed">
              σ targets derive from <span className="text-gray-400">TP × PREMIUM_MULT / Δ</span> with PREMIUM_MULT = 1.82 (30 DTE ATM ≈ 1.82×σ_daily) and Δ = 0.5.
              Hold = 15 trading bars; base==stress (breadth-conditional TP/SL tested + rejected at N=500, 2026-08-10 — see tpsl_refine_2026_08).
              <br />Anchor ref = 30 cal days. Scale √(W/30): 3d ≈ 0.32×{' · '}5d ≈ 0.41×{' · '}7d ≈ 0.48×{' · '}15d ≈ 0.71×{' · '}30d = 1.0× (anchor){' · '}60d ≈ 1.41×.
              <br />1d = direction only (no barrier).
            </div>
          </>
        ) : (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-300 font-sans">
              30 DTE H5 assessment anchor <span className="text-gray-500 normal-case tracking-normal">(Phase H5_HOLD15_H40 era, frozen — not the live strategy; live = TP+10/SL−100 since 2026-08-10)</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-green-300">Calls — TP_BASE +35% / SL_BASE −30% on premium</span>
              <span>win if high ≥ <span className="text-gray-100">entry × (1 + 1.274σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if low ≤ <span className="text-gray-400">entry × (1 − 1.092σ·√(W/30))</span></span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-red-300">Puts — TP +35% / SL −20% on premium</span>
              <span>win if low ≤ <span className="text-gray-100">entry × (1 − 1.274σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if high ≥ <span className="text-gray-400">entry × (1 + 0.728σ·√(W/30))</span></span>
            </div>
            <div className="text-gray-500 pt-1 border-t border-white/[0.06] leading-relaxed">
              σ targets derive from <span className="text-gray-400">TP × PREMIUM_MULT / Δ</span> with PREMIUM_MULT = 1.82 (30 DTE ATM ≈ 1.82×σ_daily) and Δ = 0.5.
              Hold = 15 trading bars; breadth-adaptive widens to TP +42% / SL −40% when breadth_score ≤ 40 (30 DTE post-v32_optim; 15 DTE: +40%/−35% @ ≤50). BASE shown here.
              <br />Anchor ref = 30 cal days. Scale √(W/30): 3d ≈ 0.32×{' · '}5d ≈ 0.41×{' · '}7d ≈ 0.48×{' · '}15d ≈ 0.71×{' · '}30d = 1.0× (anchor){' · '}60d ≈ 1.41×.
              <br />1d = direction only (no barrier).
            </div>
          </>
        )}
        <div className="pt-1 border-t border-white/[0.06]">
          <span className="text-gray-400">Break-even (with realistic per-exit slippage):</span>{' '}
          <span className="text-trading-green-300">calls {TP_CALL_BE}%</span>{' · '}
          <span className="text-trading-red-300">puts {TP_PUT_BE}%</span>.
          Cell color = margin above the side's BE.
        </div>
      </div>
    </div>
    {tpLoading && !tpData
      ? <p className="text-gray-500 text-sm text-center py-8">Loading…</p>
      : tpError && !tpData
        ? <p className="text-trading-red-400 text-sm text-center py-8 whitespace-pre-line">{tpError}</p>
        : !tpData?.buckets?.length
          ? <p className="text-gray-500 text-sm text-center py-8">No TP% data yet — run <span className="font-mono text-gray-400">trader assess --force</span>.</p>
          : <WinRateTable
              buckets={(() => {
                const win = (tpData?.windows || []).find(w => w.label === selectedWindow);
                const src = win?.buckets || tpData.buckets || [];
                return src.filter(b => {
                  if (!CALL_BUCKETS.has(b.bucket) && !PUT_BUCKETS.has(b.bucket)) return false;
                  if (b.side === 'call' && !showCalls) return false;
                  if (b.side === 'put'  && !showPuts)  return false;
                  return true;
                });
              })()}
              primaryPeriods={dteCfg.primaries}
              prevByBucket={activeTpByBucket}
              colorFn={tpRateColor}
              glowFn={(v, side) => {
                // Mirror winRateGlowStyle but anchor the glow ramp to a high TP%
                // (≥ BE + 30pp). Visually tells you when a bucket is exceptional.
                if (v == null) return undefined;
                const be = side === 'put' ? TP_PUT_BE : TP_CALL_BE;
                const trigger = be + 30;
                if (v < trigger) return undefined;
                const ex = Math.min(1, (v - trigger) / 10);
                const [r, g, b] = WINRATE_GLOW_RGB;
                const k = (mult) => (ex * mult).toFixed(3);
                return {
                  boxShadow: [
                    `0 0 0 1px rgba(${r},${g},${b},${k(0.3)})`,
                    `inset 0 1px 0 0 rgba(${r},${g},${b},${k(0.1)})`,
                    `0 0 6px rgba(${r},${g},${b},${k(0.48)})`,
                    `0 0 14px rgba(${r},${g},${b},${k(0.3)})`,
                    `0 0 26px rgba(${r},${g},${b},${k(0.16)})`,
                  ].join(', '),
                };
              }}
            />
    }
  </>
  );
}
